import { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  CalendarX,
  Clock,
  Lock,
  Plus,
  Printer,
  Send,
  Unlock,
  X,
} from 'lucide-react';
import {
  AdminPage,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Panel,
  Skeleton,
  StatusBadge,
} from '@/design/components';
import { cn } from '@/lib/cn';
import { useMe } from '@/auth/useMe';
import { usePerm } from '@/access/usePerm';
import { formatDate, formatDateTime, weekdayName } from '@/lib/dates';
import { errorMessage } from '@/lib/errors';
import { label, schedulePeriodStatus } from '@/lib/i18n/he';
import {
  cellWarnings,
  closureFor,
  constraintFor,
  hoursLabel,
  useBoard,
  usePeriodMutations,
  type Board,
  type BoardAssignment,
  type BoardClosure,
} from './api';
import { PublishDialog } from './PublishDialog';
import { SuggestButton } from './SuggestButton';

/** מסך בניית הסידור (PRD 5.4.4). */
export function BoardPage() {
  const { periodId = '' } = useParams();
  const { me } = useMe();
  const canManage = usePerm('schedule.manage');
  const board = useBoard(periodId);
  const {
    assign,
    setHours,
    unassign,
    publish,
    schedulePublish,
    cancelPublish,
    reopenSchedule,
    closeDay,
    reopenDay,
  } = usePeriodMutations(periodId, me?.employeeId);

  const [picking, setPicking] = useState<{
    date: string;
    shiftTypeId: string;
  } | null>(null);
  /** היום שעליו פתוח דיאלוג הסגירה */
  const [closing, setClosing] = useState<string | null>(null);
  /** השיבוץ שעליו פתוחה עריכת שעות */
  const [editingHours, setEditingHours] = useState<BoardAssignment | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** דיאלוג מועד הפרסום פתוח */
  const [publishing, setPublishing] = useState(false);
  /** אישור הפתיחה מחדש פתוח */
  const [reopening, setReopening] = useState(false);

  if (!canManage) return <Navigate to="/admin" replace />;
  if (board.isPending) return <Skeleton className="m-4 h-64 lg:m-8" />;
  if (board.isError) return <ErrorState onRetry={() => void board.refetch()} />;
  if (!board.data) {
    return <EmptyState title="התקופה לא נמצאה" description="ייתכן שאין לך הרשאה לסניף הזה." />;
  }

  const data = board.data;
  const isPublished = data.period.status === 'published';
  const scheduledAt = data.period.publish_at ?? null;

  async function run(action: () => Promise<unknown>) {
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(errorMessage(caught));
    }
  }

  const cellProps = (date: string, shiftTypeId: string) => ({
    board: data,
    date,
    shiftTypeId,
    isPublished,
    onAdd: () => setPicking({ date, shiftTypeId }),
    onRemove: (assignmentId: string) => run(() => unassign.mutateAsync(assignmentId)),
    onEditHours: (assignment: BoardAssignment) => setEditingHours(assignment),
  });

  return (
    <AdminPage
      title="בניית סידור"
      subtitle={`${data.branch_name ?? ''} · ${formatDate(data.period.start_date)} עד ${formatDate(data.period.end_date)}`}
      backTo="/admin/schedule"
      width="full"
      actions={
        <>
          <Link to={`/admin/schedule/${periodId}/print`} className="no-underline">
            <Button variant="secondary">
              <Printer size={20} strokeWidth={1.75} aria-hidden="true" />
              הדפסה
            </Button>
          </Link>
          {!isPublished && (
            <SuggestButton
              periodId={periodId}
              shiftNames={Object.fromEntries(
                data.shift_types.map((shift) => [shift.id, shift.name]),
              )}
              onApplied={(added) =>
                setNotice(
                  added === 1
                    ? 'משמרת אחת נטענה כטיוטה. אפשר לערוך ואז לפרסם.'
                    : `${added} משמרות נטענו כטיוטה. אפשר לערוך ואז לפרסם.`,
                )
              }
            />
          )}
          <StatusBadge tone={isPublished ? 'good' : 'warn'}>
            {label(schedulePeriodStatus, data.period.status)}
          </StatusBadge>
          <span className="text-body-sm text-muted">
            {data.submitted.length} מתוך {data.staff.length} הגישו אילוצים
          </span>
          {!isPublished && data.assignments.length > 0 && (
            <Button variant="primary" onClick={() => setPublishing(true)}>
              <Send size={20} strokeWidth={1.75} aria-hidden="true" className="rtl:-scale-x-100" />
              פרסום הסידור
            </Button>
          )}

          {/*
            סידור מפורסם נעול לשינויים, וההודעה שהנעילה מחזירה
            אומרת לפתוח אותו מחדש. זה הכפתור שעושה את זה.
          */}
          {isPublished && (
            <Button variant="secondary" onClick={() => setReopening(true)}>
              <Unlock size={20} strokeWidth={1.75} aria-hidden="true" />
              פתיחה מחדש לשינויים
            </Button>
          )}
        </>
      }
    >
      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="m-0 text-body-sm text-good">
          {notice}
        </p>
      )}

      {/*
        תזמון פתוח נאמר במפורש, עם דרך אחת לבטל אותו. מנהל שערך
        סידור ולא יודע שהוא עומד לצאת בעוד שעתיים הוא בדיוק המצב
        שהתזמון אמור למנוע.
      */}
      {!isPublished && scheduledAt && (
        <div className="flex flex-wrap items-center gap-2 rounded-sm border border-ink bg-olive-soft p-2">
          <Clock size={18} strokeWidth={1.75} aria-hidden="true" className="shrink-0 text-ink" />
          <span className="text-body-sm text-ink">
            הפרסום מתוזמן ליום {weekdayName(scheduledAt)}, {formatDateTime(scheduledAt)}. אפשר
            להמשיך לערוך עד אז.
          </span>
          <span className="grow" />
          <Button
            variant="secondary"
            disabled={cancelPublish.isPending}
            onClick={() =>
              run(async () => {
                await cancelPublish.mutateAsync();
                setNotice('התזמון בוטל. הסידור לא יתפרסם עד שתפרסמ/י אותו.');
              })
            }
          >
            <X size={16} strokeWidth={2} aria-hidden="true" />
            ביטול התזמון
          </Button>
        </div>
      )}

      {reopening && (
        <ConfirmDialog
          tone="warn"
          title="פתיחת הסידור מחדש"
          confirmLabel="פתיחה מחדש לשינויים"
          isPending={reopenSchedule.isPending}
          onCancel={() => setReopening(false)}
          onConfirm={() =>
            run(async () => {
              await reopenSchedule.mutateAsync();
              setReopening(false);
              setNotice('הסידור נפתח לשינויים. הוא יחזור לעובדים בפרסום הבא.');
            })
          }
          body={
            <>
              <p className="m-0">
                השבוע {formatDate(data.period.start_date)} עד {formatDate(data.period.end_date)}{' '}
                יחזור למצב עבודה, ותוכל/י לערוך אותו שוב.
              </p>
              <p className="m-0">
                עד הפרסום הבא המשמרות ייעלמו מהאפליקציה של העובדים, גם ממי שכבר ראה אותן.
              </p>
            </>
          }
        />
      )}

      {publishing && (
        <PublishDialog
          weekLabel={`${formatDate(data.period.start_date)} עד ${formatDate(data.period.end_date)}`}
          shifts={data.assignments.length}
          isPending={publish.isPending || schedulePublish.isPending}
          onCancel={() => setPublishing(false)}
          onPublishNow={() =>
            run(async () => {
              const count = await publish.mutateAsync();
              setPublishing(false);
              setNotice(`הסידור פורסם. נשלחה התראה ל ${count} עובדים.`);
            })
          }
          onSchedule={(at) =>
            run(async () => {
              await schedulePublish.mutateAsync(at);
              setPublishing(false);
              // בלי הודעה: הפס שמופיע מתחת אומר את זה, ונשאר
              setNotice(null);
            })
          }
        />
      )}

      {data.shift_types.length === 0 && (
        <EmptyState
          title="אין סוגי משמרות לסניף"
          description="צריך להגדיר אותם בהגדרות לפני בניית סידור."
        />
      )}

      {data.shift_types.length > 0 && (
        <>
          {/*
            דסקטופ: השבוע כולו כלוח אחד, ימים כעמודות ומשמרות כשורות.
            זאת הצורה שבה מנהל קורא סידור, ורק ככה רואים חור באמצע השבוע
            בלי לגלול.
          */}
          <Panel className="hidden lg:block" bodyClassName="p-0">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th
                      scope="col"
                      className="sticky start-0 z-10 w-40 border-b border-line bg-cream px-4 py-2.5 text-start text-label font-medium text-muted"
                    >
                      משמרת
                    </th>
                    {data.days.map((date) => {
                      const dayClosure = closureFor(data, date, null);
                      return (
                        <th
                          key={date}
                          scope="col"
                          className={cn(
                            'min-w-44 border-b border-s border-line px-3 py-2.5 text-start',
                            dayClosure?.is_closed ? 'bg-charcoal/5' : 'bg-cream',
                          )}
                        >
                          <span className="flex items-baseline justify-between gap-2">
                            <span className="block text-body-sm font-semibold text-ink">
                              {weekdayName(date)}
                            </span>
                            {!isPublished && (
                              <button
                                type="button"
                                onClick={() => setClosing(date)}
                                aria-label={`הגדרת יום ${weekdayName(date)}`}
                                title="סגירה או הערה על היום"
                                className="cursor-pointer bg-transparent text-muted hover:text-ink"
                              >
                                <CalendarX size={15} strokeWidth={1.75} aria-hidden="true" />
                              </button>
                            )}
                          </span>
                          <span className="block text-label font-normal text-muted tabular">
                            {formatDate(date)}
                          </span>
                          {dayClosure && <ClosureTag closure={dayClosure} />}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {data.shift_types.map((shift) => (
                    <tr key={shift.id}>
                      <th
                        scope="row"
                        className="sticky start-0 z-10 border-b border-line bg-surface px-4 py-3 text-start align-top"
                      >
                        <span className="flex items-center gap-2 text-body-sm font-semibold text-ink">
                          {shift.color && (
                            <span
                              aria-hidden="true"
                              className="size-3 shrink-0 rounded-pill"
                              style={{ backgroundColor: shift.color }}
                            />
                          )}
                          {shift.name}
                        </span>
                        <span className="block text-label font-normal text-muted tabular" dir="ltr">
                          {shift.start_time.slice(0, 5)}–{shift.end_time.slice(0, 5)}
                        </span>
                      </th>

                      {data.days.map((date) => (
                        <td key={date} className="border-b border-s border-line p-2 align-top">
                          <Cell {...cellProps(date, shift.id)} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          {/* טלפון: יום אחרי יום. לוח של שבע עמודות לא נקרא במסך צר */}
          <div className="grid gap-4 lg:hidden">
            {data.days.map((date) => (
              <section key={date} className="grid gap-2">
                <h2 className="m-0 font-display text-display-m font-semibold">
                  {weekdayName(date)}{' '}
                  <span className="text-body-sm font-normal text-muted tabular">
                    {formatDate(date)}
                  </span>
                </h2>

                {data.shift_types.map((shift) => (
                  <div key={shift.id} className="grid gap-2 rounded-md border border-line p-3">
                    <span className="flex items-center gap-2 text-title">
                      {shift.color && (
                        <span
                          aria-hidden="true"
                          className="size-3 rounded-pill"
                          style={{ backgroundColor: shift.color }}
                        />
                      )}
                      {shift.name}
                      <span className="text-body-sm text-muted tabular" dir="ltr">
                        {shift.start_time.slice(0, 5)}–{shift.end_time.slice(0, 5)}
                      </span>
                    </span>
                    <Cell {...cellProps(date, shift.id)} />
                  </div>
                ))}
              </section>
            ))}
          </div>
        </>
      )}

      {editingHours && (
        <HoursDialog
          board={data}
          assignment={editingHours}
          onClose={() => setEditingHours(null)}
          onSave={(startTime, endTime) =>
            run(async () => {
              await setHours.mutateAsync({
                id: editingHours.id,
                start_time: startTime,
                end_time: endTime,
              });
              setEditingHours(null);
            })
          }
        />
      )}

      {closing && (
        <ClosureDialog
          board={data}
          date={closing}
          onClose={() => setClosing(null)}
          onSave={(input) =>
            run(async () => {
              await closeDay.mutateAsync({ ...input, branch_id: data.period.branch_id });
              setClosing(null);
            })
          }
          onReopen={(shiftTypeId) =>
            run(async () => {
              await reopenDay.mutateAsync({
                branch_id: data.period.branch_id,
                date: closing,
                shift_type_id: shiftTypeId,
              });
              setClosing(null);
            })
          }
        />
      )}

      {picking && (
        <StaffPicker
          board={data}
          date={picking.date}
          shiftTypeId={picking.shiftTypeId}
          onClose={() => setPicking(null)}
          onPick={(employeeId, startTime, endTime) =>
            run(async () => {
              await assign.mutateAsync({
                branch_id: data.period.branch_id,
                date: picking.date,
                shift_type_id: picking.shiftTypeId,
                employee_id: employeeId,
                start_time: startTime,
                end_time: endTime,
              });
              setPicking(null);
            })
          }
        />
      )}
    </AdminPage>
  );
}

/** תא אחד בלוח: המשובצים, אזהרות, וכפתור שיבוץ. */
function Cell({
  board,
  date,
  shiftTypeId,
  isPublished,
  onAdd,
  onRemove,
  onEditHours,
}: {
  board: Board;
  date: string;
  shiftTypeId: string;
  isPublished: boolean;
  onAdd: () => void;
  onRemove: (assignmentId: string) => void;
  onEditHours: (assignment: BoardAssignment) => void;
}) {
  const assigned = board.assignments.filter(
    (a) => a.date === date && a.shift_type_id === shiftTypeId,
  );
  const warnings = cellWarnings(board, date, shiftTypeId);
  const closure = closureFor(board, date, shiftTypeId);

  // תא סגור לא מציג "אף אחד לא משובץ" ולא כפתור שיבוץ: אין מה לשבץ בו
  if (closure?.is_closed) {
    return (
      <div className="grid min-h-24 content-center justify-items-center gap-1 rounded-sm border border-dashed border-muted/50 bg-charcoal/5 p-2 text-center">
        <Lock size={16} strokeWidth={1.75} aria-hidden="true" className="text-muted" />
        <span className="text-label font-medium text-ink">סגור</span>
        <span className="text-label text-muted">{closure.label}</span>
        {closure.note && <span className="text-label text-muted">{closure.note}</span>}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'grid min-h-24 content-start gap-2 rounded-sm border p-2',
        warnings.some((w) => w.kind === 'blocked')
          ? 'border-bad bg-bad/5'
          : warnings.length > 0
            ? 'border-warn bg-warn/5'
            : 'border-transparent',
      )}
    >
      {assigned.length === 0 ? (
        <p className="m-0 text-label text-muted">אף אחד לא משובץ</p>
      ) : (
        <ul className="flex list-none flex-wrap gap-1 p-0">
          {assigned.map((assignment) => {
            const person = board.staff.find((s) => s.id === assignment.employee_id);
            const constraint = constraintFor(board, assignment.employee_id, date, shiftTypeId);

            return (
              <li
                key={assignment.id}
                className={cn(
                  'flex items-center gap-1 rounded-pill border bg-surface px-2 py-0.5 text-label',
                  constraint?.is_blocking
                    ? 'border-bad text-bad'
                    : constraint
                      ? 'border-warn text-warn'
                      : 'border-muted text-ink',
                )}
              >
                {isPublished ? (
                  <span className="grid">
                    <span>{person?.name ?? 'עובד/ת'}</span>
                    {hoursLabel(assignment) && (
                      <span className="text-label font-normal opacity-75 tabular">
                        {hoursLabel(assignment)}
                      </span>
                    )}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => onEditHours(assignment)}
                    aria-label={`שעות של ${person?.name ?? 'העובד/ת'}`}
                    title="שינוי שעות"
                    className="grid cursor-pointer bg-transparent text-start text-current"
                  >
                    <span>{person?.name ?? 'עובד/ת'}</span>
                    <span className="text-label font-normal opacity-75 tabular">
                      {hoursLabel(assignment) || 'לפי הרצועה'}
                    </span>
                  </button>
                )}
                {!isPublished && (
                  <button
                    type="button"
                    aria-label={`הסרת ${person?.name ?? ''} מהמשמרת`}
                    onClick={() => onRemove(assignment.id)}
                    className="cursor-pointer bg-transparent text-current"
                  >
                    <X size={14} strokeWidth={1.75} aria-hidden="true" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {warnings.map((warning) => (
        <p
          key={warning.text}
          className={cn(
            'm-0 flex items-start gap-1 text-label',
            warning.kind === 'blocked' ? 'text-bad' : 'text-warn',
          )}
        >
          <AlertTriangle
            size={13}
            strokeWidth={1.75}
            aria-hidden="true"
            className="mt-0.5 shrink-0"
          />
          {warning.text}
        </p>
      ))}

      {!isPublished && (
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex min-h-9 cursor-pointer items-center gap-1 justify-self-start rounded-sm bg-transparent px-1 text-label text-muted hover:text-ink"
        >
          <Plus size={16} strokeWidth={1.75} aria-hidden="true" />
          שיבוץ
        </button>
      )}
    </div>
  );
}

/**
 * בחירת עובד לשיבוץ. האילוץ מוצג ליד כל שם, כדי שהמנהל יראה לפני
 * הלחיצה מי סימן "לא יכול" ומי רק מעדיף שלא.
 */
function StaffPicker({
  board,
  date,
  shiftTypeId,
  onClose,
  onPick,
}: {
  board: Board;
  date: string;
  shiftTypeId: string;
  onClose: () => void;
  onPick: (employeeId: string, startTime: string | null, endTime: string | null) => void;
}) {
  const band = board.shift_types.find((t) => t.id === shiftTypeId);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const field = 'min-h-11 rounded-sm border border-muted bg-surface px-2 text-body tabular';

  const alreadyHere = new Set(
    board.assignments
      .filter((a) => a.date === date && a.shift_type_id === shiftTypeId)
      .map((a) => a.employee_id),
  );

  const options = board.staff
    .filter((person) => !alreadyHere.has(person.id))
    .map((person) => ({
      person,
      constraint: constraintFor(board, person.id, date, shiftTypeId),
    }))
    .sort((a, b) => {
      const weight = (c: typeof a.constraint) => (c?.is_blocking ? 2 : c ? 1 : 0);
      return (
        weight(a.constraint) - weight(b.constraint) || a.person.name.localeCompare(b.person.name)
      );
    });

  return (
    <div className="fixed inset-0 z-30 grid items-end bg-charcoal/40 p-0 sm:place-items-center sm:p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="בחירת עובד/ת למשמרת"
        className="grid max-h-[80dvh] w-full gap-3 overflow-y-auto rounded-t-lg bg-surface p-4 sm:max-w-md sm:rounded-lg"
      >
        <div className="flex items-center justify-between gap-2">
          <h2 className="m-0 font-display text-display-m font-semibold">
            {weekdayName(date)}, {board.shift_types.find((s) => s.id === shiftTypeId)?.name}
          </h2>
          <button
            type="button"
            aria-label="סגירה"
            onClick={onClose}
            className="grid size-11 cursor-pointer place-items-center bg-transparent text-ink"
          >
            <X size={20} strokeWidth={1.75} aria-hidden="true" />
          </button>
        </div>

        {/*
          שעות אישיות. ריק = לפי הרצועה, וזה המצב הנפוץ. הרשת כותבת
          היום "עד 16:00" או "מ 15", ולכן אפשר למלא רק צד אחד.
        */}
        <div className="grid grid-cols-2 gap-2">
          <label className="grid gap-1 text-label font-medium">
            מתחיל/ה
            <input
              type="time"
              dir="ltr"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              placeholder={band?.start_time.slice(0, 5)}
              className={field}
            />
          </label>
          <label className="grid gap-1 text-label font-medium">
            מסיים/ת
            <input
              type="time"
              dir="ltr"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              placeholder={band?.end_time.slice(0, 5)}
              className={field}
            />
          </label>
        </div>
        <p className="m-0 text-body-sm text-muted">
          ריק = לפי שעות {band?.name}
          {band && (
            <span className="tabular" dir="ltr">
              {' '}
              ({band.start_time.slice(0, 5)}–{band.end_time.slice(0, 5)})
            </span>
          )}
        </p>

        {options.length === 0 ? (
          <p className="m-0 text-body-sm text-muted">כל עובדי הסניף כבר משובצים כאן.</p>
        ) : (
          <ul className="grid list-none gap-1 p-0">
            {options.map(({ person, constraint }) => (
              <li key={person.id}>
                <button
                  type="button"
                  onClick={() => onPick(person.id, from || null, to || null)}
                  className="flex min-h-11 w-full cursor-pointer items-center justify-between gap-2 rounded-sm border border-line px-3 text-start text-body hover:bg-cream"
                >
                  <span className="grid">
                    <span>{person.name}</span>
                    {person.job_title && (
                      <span className="text-label text-muted">{person.job_title}</span>
                    )}
                  </span>

                  {constraint && (
                    <StatusBadge tone={constraint.is_blocking ? 'bad' : 'warn'}>
                      {constraint.name}
                    </StatusBadge>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** התווית שמופיעה בראש היום: "סגור — יום הבחירות" או הערה בלבד. */
function ClosureTag({ closure }: { closure: BoardClosure }) {
  return (
    <span
      className={cn(
        'mt-1 inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-label font-medium',
        closure.is_closed ? 'bg-charcoal/10 text-ink' : 'bg-butter text-ink',
      )}
    >
      {closure.is_closed && <Lock size={12} strokeWidth={2} aria-hidden="true" />}
      {closure.is_closed ? `סגור — ${closure.label}` : closure.label}
    </span>
  );
}

/**
 * סגירה או הערה על יום מסוים, מתוך הלוח. כלל שחוזר כל שבוע (שבת סגורה)
 * נקבע במסך ההגדרות ולא כאן, כדי שלא ייווצר בטעות כלל קבוע מתוך
 * עבודה על שבוע אחד.
 */
function ClosureDialog({
  board,
  date,
  onClose,
  onSave,
  onReopen,
}: {
  board: Board;
  date: string;
  onClose: () => void;
  onSave: (input: {
    date: string;
    weekday: null;
    shift_type_id: string | null;
    is_closed: boolean;
    label: string;
    note: string | null;
  }) => void;
  onReopen: (shiftTypeId: string | null) => void;
}) {
  const existing = closureFor(board, date, null);
  const [scope, setScope] = useState<string>('all');
  const [isClosed, setIsClosed] = useState(true);
  const [label, setLabel] = useState('');
  const [note, setNote] = useState('');

  const field = 'min-h-11 rounded-sm border border-muted bg-surface px-3 text-body text-ink';

  return (
    <div className="fixed inset-0 z-30 grid items-end bg-charcoal/40 p-0 sm:place-items-center sm:p-4">
      <form
        role="dialog"
        aria-modal="true"
        aria-label={`הגדרת ${weekdayName(date)}`}
        className="grid max-h-[85dvh] w-full gap-3 overflow-y-auto rounded-t-lg bg-surface p-4 sm:max-w-md sm:rounded-lg"
        onSubmit={(event) => {
          event.preventDefault();
          onSave({
            date,
            weekday: null,
            shift_type_id: scope === 'all' ? null : scope,
            is_closed: isClosed,
            label: label.trim(),
            note: note.trim() || null,
          });
        }}
      >
        <div className="flex items-center justify-between gap-2">
          <h2 className="m-0 font-display text-display-m font-semibold">
            {weekdayName(date)} {formatDate(date)}
          </h2>
          <button
            type="button"
            aria-label="סגירה"
            onClick={onClose}
            className="grid size-11 cursor-pointer place-items-center bg-transparent text-ink"
          >
            <X size={20} strokeWidth={1.75} aria-hidden="true" />
          </button>
        </div>

        {existing && (
          <div className="grid gap-2 rounded-md border border-line p-3">
            <span className="text-body-sm text-ink">
              כרגע מוגדר: <strong>{existing.label}</strong>
              {existing.is_closed ? ' (סגור)' : ' (הערה)'}
            </span>
            <Button variant="secondary" onClick={() => onReopen(existing.shift_type_id)}>
              ביטול ההגדרה והחזרת היום לעבודה
            </Button>
          </div>
        )}

        <div className="grid gap-1">
          <label htmlFor="closure-scope" className="text-label font-medium">
            על מה זה חל
          </label>
          <select
            id="closure-scope"
            value={scope}
            onChange={(event) => setScope(event.target.value)}
            className={field}
          >
            <option value="all">כל היום</option>
            {board.shift_types.map((shift) => (
              <option key={shift.id} value={shift.id}>
                משמרת {shift.name} בלבד
              </option>
            ))}
          </select>
        </div>

        <fieldset className="grid gap-2 border-0 p-0">
          <legend className="text-label font-medium">מה קורה</legend>
          <label className="flex min-h-11 items-center gap-2 text-body">
            <input
              type="radio"
              name="closure-kind"
              checked={isClosed}
              onChange={() => setIsClosed(true)}
              className="size-5"
            />
            סגור, אי אפשר לשבץ
          </label>
          <label className="flex min-h-11 items-center gap-2 text-body">
            <input
              type="radio"
              name="closure-kind"
              checked={!isClosed}
              onChange={() => setIsClosed(false)}
              className="size-5"
            />
            עובדים כרגיל, רק להציג הערה
          </label>
        </fieldset>

        <div className="grid gap-1">
          <label htmlFor="closure-label" className="text-label font-medium">
            מה לכתוב *
          </label>
          <input
            id="closure-label"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder="יום הבחירות, ליל הסדר, ספירת מלאי"
            className={field}
          />
          <p className="m-0 text-body-sm text-muted">זה מה שהעובדים יראו על היום.</p>
        </div>

        <div className="grid gap-1">
          <label htmlFor="closure-note" className="text-label font-medium">
            פירוט (לא חובה)
          </label>
          <input
            id="closure-note"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="סוגרים ב 14:00"
            className={field}
          />
        </div>

        <div className="flex gap-2">
          <Button type="submit" variant="primary" disabled={label.trim() === ''}>
            שמירה
          </Button>
          <Button variant="secondary" onClick={onClose}>
            ביטול
          </Button>
        </div>
      </form>
    </div>
  );
}

/**
 * שינוי שעות של שיבוץ קיים.
 *
 * ריק = לפי הרצועה, וזה גם הדרך לנקות שעות שנקבעו בטעות. כל צד
 * עומד בפני עצמו, כי "עד 16:00" בלי לגעת בשעת ההתחלה הוא המקרה
 * הנפוץ ביותר בגיליון שלהם.
 */
function HoursDialog({
  board,
  assignment,
  onClose,
  onSave,
}: {
  board: Board;
  assignment: BoardAssignment;
  onClose: () => void;
  onSave: (startTime: string | null, endTime: string | null) => void;
}) {
  const band = board.shift_types.find((t) => t.id === assignment.shift_type_id);
  const person = board.staff.find((s) => s.id === assignment.employee_id);
  const [from, setFrom] = useState((assignment.start_time ?? '').slice(0, 5));
  const [to, setTo] = useState((assignment.end_time ?? '').slice(0, 5));

  const field = 'min-h-11 rounded-sm border border-muted bg-surface px-2 text-body tabular';

  return (
    <div className="fixed inset-0 z-30 grid items-end bg-charcoal/40 p-0 sm:place-items-center sm:p-4">
      <form
        role="dialog"
        aria-modal="true"
        aria-label="שינוי שעות"
        className="grid w-full gap-3 rounded-t-lg bg-surface p-4 sm:max-w-sm sm:rounded-lg"
        onSubmit={(event) => {
          event.preventDefault();
          onSave(from || null, to || null);
        }}
      >
        <div className="flex items-center justify-between gap-2">
          <h2 className="m-0 font-display text-display-m font-semibold">
            {person?.name ?? 'שעות'}
          </h2>
          <button
            type="button"
            aria-label="סגירה"
            onClick={onClose}
            className="grid size-11 cursor-pointer place-items-center bg-transparent text-ink"
          >
            <X size={20} strokeWidth={1.75} aria-hidden="true" />
          </button>
        </div>

        <p className="m-0 text-body-sm text-muted">
          {weekdayName(assignment.date)} {formatDate(assignment.date)}
          {band && <span> · {band.name}</span>}
        </p>

        <div className="grid grid-cols-2 gap-2">
          <label className="grid gap-1 text-label font-medium">
            מתחיל/ה
            <input
              type="time"
              dir="ltr"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              className={field}
            />
          </label>
          <label className="grid gap-1 text-label font-medium">
            מסיים/ת
            <input
              type="time"
              dir="ltr"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              className={field}
            />
          </label>
        </div>

        <p className="m-0 text-body-sm text-muted">
          ריק = לפי שעות {band?.name}
          {band && (
            <span className="tabular" dir="ltr">
              {' '}
              ({band.start_time.slice(0, 5)}–{band.end_time.slice(0, 5)})
            </span>
          )}
        </p>

        <div className="flex flex-wrap gap-2">
          <Button type="submit" variant="primary">
            שמירה
          </Button>
          {(from || to) && (
            <Button variant="secondary" onClick={() => onSave(null, null)}>
              חזרה לשעות הרצועה
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            ביטול
          </Button>
        </div>
      </form>
    </div>
  );
}
